import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../hooks/useAuth';
import type { Poll } from '../hooks/usePolls';
import { Trash2 } from 'lucide-react';

type PollCardProps = {
  poll: Poll;
  onVote: (pollId: string, optionId: string) => Promise<void>;
  onClose?: (pollId: string) => Promise<void>;
  onDelete?: (pollId: string) => Promise<void>;
  isAuthor?: boolean;
};

export default function PollCard({ poll, onVote, onClose, onDelete, isAuthor }: PollCardProps) {
  const { user } = useAuth();
  const [selectedOption, setSelectedOption] = React.useState<string | null>(null);
  const [isVoting, setIsVoting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [isDeleting, setIsDeleting] = React.useState(false);

  const hasVoted = poll.options.some(opt => opt.has_voted);
  const isExpired = new Date(poll.ends_at) < new Date();
  const canVote = user && !hasVoted && !isExpired && !poll.closed;

  const handleVote = async () => {
    if (!selectedOption || !canVote) return;

    try {
      setIsVoting(true);
      setError(null);
      await onVote(poll.id, selectedOption);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to vote');
    } finally {
      setIsVoting(false);
    }
  };

  const handleClose = async () => {
    if (!onClose) return;

    try {
      await onClose(poll.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to close poll');
    }
  };

  const handleDelete = async () => {
    if (!onDelete || !window.confirm('Are you sure you want to delete this poll? This action cannot be undone.')) {
      return;
    }

    try {
      setIsDeleting(true);
      await onDelete(poll.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete poll');
      setIsDeleting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-xl font-semibold h3-font mb-1">{poll.title}</h3>
          {poll.description && (
            <p className="text-gray-600 mb-2">{poll.description}</p>
          )}
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span>
              {poll.closed
                ? 'Closed'
                : isExpired
                ? 'Expired'
                : `Ends ${formatDistanceToNow(new Date(poll.ends_at), { addSuffix: true })}`}
            </span>
            <span>{poll.total_votes} votes</span>
          </div>
        </div>

        {isAuthor && (
          <div className="flex items-center gap-2">
            {!poll.closed && !isExpired && (
              <button
                onClick={handleClose}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                Close Poll
              </button>
            )}
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="text-sm text-red-600 hover:text-red-700 disabled:opacity-50"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="space-y-4">
        {poll.type === 'cover_comparison' ? (
          <div className="grid grid-cols-2 gap-4">
            {poll.options.map((option) => (
              <div
                key={option.id}
                className={`relative rounded-lg overflow-hidden ${
                  canVote ? 'cursor-pointer hover:ring-4 hover:ring-indigo-200' : ''
                } ${
                  selectedOption === option.id && canVote
                    ? 'ring-4 ring-indigo-500'
                    : option.has_voted
                    ? 'ring-4 ring-green-500'
                    : ''
                }`}
                onClick={() => canVote && setSelectedOption(option.id)}
              >
                <div className="relative aspect-[3/4]">
                  {option.image_url && (
                    <img
                      src={option.image_url}
                      alt="Cover option"
                      className="w-full h-full object-contain"
                    />
                  )}
                </div>
                {(!canVote || hasVoted) && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                    <span className="text-white text-xl font-bold">
                      {Math.round((option.votes_count / poll.total_votes) * 100)}%
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {poll.options.map((option) => (
              <button
                key={option.id}
                onClick={() => canVote && setSelectedOption(option.id)}
                disabled={!canVote}
                className={`w-full p-4 text-left rounded-lg transition-colors ${
                  selectedOption === option.id && canVote
                    ? 'bg-indigo-600 text-white'
                    : option.has_voted
                    ? 'bg-green-600 text-white'
                    : canVote
                    ? 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                    : 'bg-gray-100 text-gray-900'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span>{option.text}</span>
                  {(!canVote || hasVoted) && (
                    <span className="font-semibold">
                      {Math.round((option.votes_count / poll.total_votes) * 100)}%
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        {canVote && selectedOption && (
          <button
            onClick={handleVote}
            disabled={isVoting}
            className="w-full mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {isVoting ? 'Voting...' : 'Submit Vote'}
          </button>
        )}
      </div>
    </div>
  );
}